import React, { useEffect } from "react";
import {
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  Building2,
  Check,
  Loader2,
  X,
} from "lucide-react";
import { useState } from "react";
import { useDispatch } from "react-redux";
import StepCompanyType from "./steps/StepCompanyType";
import { createInitialFormData } from "./constants/initialFormData";
import { COMPANY_TYPES } from "./constants/constant";
import StepCompanyInfo from "./steps/StepCompanyInfo";
import StepModulesSelection from "./steps/StepModulesSelection";
import { callLoader } from "@/redux/Loader/actions";
import { getModuleDetails } from "@/redux/Modules/actions";
import StepReviewSummary from "./steps/StepReviewSummary";
import {
  getMasterCompanyList,
  registerMasterCompany,
  updateMasterCompany,
} from "@/redux/Masters/company/actions";
import { GetMasterCompanyDetail } from "@/services/api/company.service";
import { toast } from "sonner";

const CompanySetup = ({
  isEdit = false,
  isOpen,
  onClose,
  onSuccess,
  companyId = null,
}) => {
  const dispatch = useDispatch();

  console.log("companyId: ", companyId);

  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(false);

  const [formData, setFormData] = useState(() => createInitialFormData());
  console.log("formData companySetup: ", formData);

  const [currentStep, setCurrentStep] = useState(1);
  const [modules, setModules] = useState([]);
  const [validationState, setValidationState] = useState({});

  const steps = [
    { num: 1, label: "Company Type" },
    { num: 2, label: "Details" },
    { num: 3, label: "Modules" },
    { num: 4, label: "Review" },
  ];

  const resetModalState = () => {
    setFormData(createInitialFormData());
    setCurrentStep(1);
    setError("");
  };

  const validateStep = (step) => {
    switch (step) {
      // =========================
      // STEP 1 : COMPANY TYPE
      // =========================

      case 1: {
        if (!formData.PROFILE_CATEGORY) {
          setError("Please select a company type");

          return false;
        }

        break;
      }

      // =========================
      // STEP 2 : COMPANY DETAILS
      // =========================

      case 2: {
        const asyncFields = [
          {
            key: "COMPANY_NAME",
            label: "Company Name",
          },
          {
            key: "PROFILE_USERNAME",
            label: "Username",
          },
          {
            key: "PROFILE_EMAILID",
            label: "Email Address",
          },
        ];
        // REQUIRED FIELDS
        const requiredFields = [
          {
            key: "COMPANY_NAME",
            label: "Company Name",
          },
          //   {
          //     key: "PROFILE_FIRSTNAME",
          //     label: "First Name",
          //   },
          //   {
          //     key: "PROFILE_LASTNAME",
          //     label: "Last Name",
          //   },
          {
            key: "PROFILE_EMAILID",
            label: "Email Address",
          },
          {
            key: "PROFILE_USERNAME",
            label: "Username",
          },
          {
            key: "PROFILE_PASSWORD",
            label: "Password",
          },
          {
            key: "CONFIRM_PASSWORD",
            label: "Confirm Password",
          },
        ];

        for (const field of requiredFields) {
          if (
            !formData[field.key] ||
            String(formData[field.key]).trim() === ""
          ) {
            setError(`${field.label} is required`);

            return false;
          }
        }

        // PASSWORD MATCH
        if (formData.PROFILE_PASSWORD !== formData.CONFIRM_PASSWORD) {
          setError("Passwords do not match");

          return false;
        }

        // PASSWORD LENGTH
        if (formData.PROFILE_PASSWORD.length < 8) {
          setError("Password must be at least 8 characters");

          return false;
        }

        // EMAIL VALIDATION
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

        if (!emailRegex.test(formData.PROFILE_EMAILID)) {
          setError("Please enter a valid email address");

          return false;
        }

        // NVOCC VALIDATION
        if (formData.PROFILE_CATEGORY === "NVOCC" && !formData.LICENCE_NUMBER) {
          setError("NVOCC License number is required");

          return false;
        }

        // =========================
        // ASYNC VALIDATION (IMPORTANT)
        // =========================

        for (const field of asyncFields) {
          const state = validationState[field.key];

          if (state?.isValid === false) {
            setError(`${field.label} already exists`);
            return false;
          }

          if (state?.isValid === null || state?.isValid === undefined) {
            setError(`Please wait while validating ${field.label}`);
            return false;
          }
        }

        break;
      }

      // =========================
      // STEP 3 : MODULES
      // =========================

      case 3: {
        if (
          !formData.SUBSCRIBED_BASEMODULE_DETAILS ||
          formData.SUBSCRIBED_BASEMODULE_DETAILS.length === 0
        ) {
          setError("Please select at least one module");

          return false;
        }

        break;
      }

      default:
        break;
    }

    setError("");

    return true;
  };

  const updateFormData = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setError("");
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep((prev) => Math.min(prev + 1, 4));
    }
  };

  const prevStep = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
    setError("");
  };

  const getCompanyDetails = async () => {
    try {
      if (!isEdit || !companyId) return;

      dispatch(callLoader(true));

      const company = await GetMasterCompanyDetail(companyId);

      console.log("company details:", company);

      if (!company) return;

      setFormData({
        ...createInitialFormData(),

        // =========================================
        // COMPANY
        // =========================================

        COMPANYID: company.COMPANYID || 0,
        COMPANY_NAME: company.COMPANYNAME || "",
        COMPANYLOGO: company.COMPANYLOGO || "",
        POWERED_BY: company.POWERED_BY || "SHIPEZ.AI",

        // =========================================
        // PROFILE
        // =========================================

        PROFILE_CATEGORY: company.PROFILE_CATEGORY || "",

        PROFILE_FIRSTNAME: company.PROFILE_FIRSTNAME || "",
        PROFILE_LASTNAME: company.PROFILE_LASTNAME || "",

        PROFILE_EMAILID: company.PROFILE_EMAILID || "",
        PROFILE_USERNAME: company.PROFILE_USERNAME || "",

        PROFILE_PASSWORD: company.PROFILE_PASSWORD || "",
        CONFIRM_PASSWORD: company.PROFILE_PASSWORD || "",

        PROFILE_PHONE: company.PROFILE_PHONE || "",

        PROFILE_COUNTRY_CODE: company.PROFILE_COUNTRY_CODE || "+1",

        PROFILE_ADDRESS1: company.PROFILE_ADDRESS1 || "",
        PROFILE_ADDRESS2: company.PROFILE_ADDRESS2 || "",

        PROFILE_ADDRESS_PRINT: company.PROFILE_ADDRESSPRINT || "",

        PROFILE_CITY: company.PROFILE_CITY || "",
        PROFILE_REGION: company.PROFILE_REGION || "",

        PROFILE_COUNTRY: company.PROFILE_COUNTRY || "USA",

        PROFILE_ZIPCODE: company.PROFILE_ZIPCODE || "",

        LICENCE_NUMBER: company.LICENCE_NUMBER || "",

        // =========================================
        // MODULES
        // =========================================

        SUBSCRIBED_BASEMODULE_DETAILS:
          company.SUBSCRIBED_BASEMODULE_DETAILS || [],

        SUBSCRIBED_BASEMODULE_DETAILS_JSON: JSON.stringify(
          company.SUBSCRIBED_BASEMODULE_DETAILS || [],
        ),
      });
    } catch (error) {
      console.error(error);

      setError("Failed to load company details");
    } finally {
      dispatch(callLoader(false));
    }
  };

  const handleSubmitCompany = async () => {
    try {
      setLoading(true);
      setError("");

      const payload = new FormData();

      payload.append("COMPANYID", isEdit ? formData.COMPANYID : 0);

      payload.append("COMPANYNAME", formData.COMPANY_NAME);
      payload.append("POWERED_BY", formData.POWERED_BY || "SHIPEZ.AI");

      payload.append("ACTION_BY", formData.ACTION_BY || "super_admin");

      payload.append("PROFILE_CATEGORY", formData.PROFILE_CATEGORY);

      // ADMIN
      payload.append("PROFILE_FIRSTNAME", formData.PROFILE_FIRSTNAME);
      payload.append("PROFILE_LASTNAME", formData.PROFILE_LASTNAME);
      payload.append("PROFILE_USERNAME", formData.PROFILE_USERNAME);
      payload.append("PROFILE_PASSWORD", formData.PROFILE_PASSWORD);

      // CONTACT
      payload.append("PROFILE_EMAILID", formData.PROFILE_EMAILID);
      payload.append("PROFILE_PHONE", formData.PROFILE_PHONE || "");
      payload.append(
        "PROFILE_COUNTRY_CODE",
        formData.PROFILE_COUNTRY_CODE || "+91",
      );

      // ADDRESS
      payload.append("PROFILE_ADDRESS1", formData.PROFILE_ADDRESS1 || "");
      payload.append("PROFILE_ADDRESS2", formData.PROFILE_ADDRESS2 || "");
      payload.append(
        "PROFILE_ADDRESSPRINT",
        `${formData.PROFILE_ADDRESS1 || ""}, ${formData.PROFILE_CITY || ""}`,
      );

      payload.append("PROFILE_CITY", formData.PROFILE_CITY || "");
      payload.append("PROFILE_REGION", formData.PROFILE_REGION || "");
      payload.append("PROFILE_COUNTRY", formData.PROFILE_COUNTRY || "USA");
      payload.append("PROFILE_ZIPCODE", formData.PROFILE_ZIPCODE || "");
      payload.append("LICENCE_NUMBER", formData.LICENCE_NUMBER || "");

      // FILE
      if (formData.FILEDATA) {
        payload.append("FILEDATA", formData.FILEDATA);
      }
      // =========================
      // MODULES
      // =========================
      const selectedModules = formData.SUBSCRIBED_BASEMODULE_DETAILS || [];

      // SEND MODULES
      payload.append(
        "SUBSCRIBED_BASEMODULE_DETAILS",
        JSON.stringify(selectedModules),
      );
      payload.append(
        "SUBSCRIBED_BASEMODULE_DETAILS_JSON",
        JSON.stringify(selectedModules),
      );

      let response;

      if (isEdit) {
        response = dispatch(updateMasterCompany(payload));
      } else {
        response = dispatch(registerMasterCompany(payload));
      }

      console.log("Created:", response);

      // SUCCESS TOAST
      toast.success(
        response?.data ||
          response?.message ||
          `Company ${isEdit ? "updated" : "created"} successfully`,
      );

      dispatch(
        getMasterCompanyList({
          IPageNo: 1,
          IPageRecords: 10,
          SearchKeyword: "",
          FilterType: "ALL",
        }),
      );

      resetModalState();
      onClose();
      setLoading(false);
    } catch (err) {
      toast.error(
        err?.message || `Failed to ${isEdit ? "update" : "create"} company`,
      );

      setError(
        err?.message || `Failed to ${isEdit ? "update" : "create"} company`,
      );
    } finally {
      setLoading(false);
    }
  };

  const getModules = async () => {
    try {
      dispatch(callLoader(true));
      const data = await dispatch(getModuleDetails());
      console.log("Modules:", data);
      setModules(data);
    } catch (error) {
      console.error(error);
      dispatch(callLoader(false));
    } finally {
      dispatch(callLoader(false));
    }
  };

  useEffect(() => {
    getModules();
  }, []);

  useEffect(() => {
    if (isOpen && isEdit && companyId) {
      getCompanyDetails();
    }
  }, [isOpen, isEdit, companyId]);

  useEffect(() => {
    if (!isOpen) {
      resetModalState();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      className={`fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4 transition-all duration-500  ${isOpen ? "opacity-100" : "opacity-0 pointer-events-none"}`}
      data-testid="company-wizard-modal"
    >
      <div
        className={`bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 w-full max-w-7xl max-h-[95vh] overflow-hidden flex flex-col  transition-all duration-500 ease-out
            ${
              isOpen
                ? "opacity-100 scale-100 translate-y-0"
                : "opacity-0 scale-95 translate-y-4"
            }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b border-slate-700 bg-gradient-to-r from-slate-700 via-slate-800 to-slate-900 shrink-0">
          {/* LEFT */}
          <div className="flex items-center gap-3 min-w-0">
            {/* ICON */}
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center shadow-lg shadow-teal-500/20 shrink-0">
              <Building2 className="w-5 h-5 text-white" />
            </div>

            {/* TITLE */}
            <div className="min-w-0">
              <h2 className="text-base sm:text-lg font-bold text-white truncate">
                {isEdit ? `Edit Company` : `Add New Company`}
              </h2>

              <p className="text-[11px] sm:text-xs text-slate-400 mt-0.5 truncate">
                Fill company details, branding, modules and admin information
              </p>
            </div>
          </div>

          {/* CLOSE */}
          <button
            onClick={onClose}
            className="
      w-9 h-9 shrink-0
      flex items-center justify-center
      rounded-xl
      text-slate-400 hover:text-white
      hover:bg-slate-700/70
      transition-all
    "
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        {/* Progress Steps */}
        <div className="px-6 py-4 bg-slate-800/50 border-b border-slate-700">
          <div className="flex items-center justify-between max-w-2xl mx-auto">
            {steps.map((step, idx) => (
              <React.Fragment key={step.num}>
                {/* STEP */}
                <div className="flex items-center shrink-0">
                  <div
                    className={`
              w-8 h-8 rounded-full
              flex items-center justify-center
              font-semibold text-sm
              transition-all duration-300 ease-in-out
              transform
              ${
                currentStep >= step.num
                  ? "bg-teal-500 text-white scale-100 shadow-lg shadow-teal-500/30"
                  : "bg-slate-700 text-slate-400 scale-95"
              }
            `}
                  >
                    {currentStep > step.num ? (
                      <Check className="w-4 h-4 animate-in zoom-in duration-300" />
                    ) : (
                      <span className="transition-all duration-300">
                        {step.num}
                      </span>
                    )}
                  </div>

                  {/* LABEL */}
                  <span
                    className={`
              ml-2 text-xs font-medium hidden sm:block
              transition-colors duration-300
              ${currentStep >= step.num ? "text-white" : "text-slate-500"}
            `}
                  >
                    {step.label}
                  </span>
                </div>

                {/* CONNECTOR */}
                {idx < steps.length - 1 && (
                  <div className="flex-1 h-0.5 mx-3 bg-slate-700 overflow-hidden rounded-full">
                    <div
                      className={`
                h-full bg-teal-500
                transition-all duration-500 ease-in-out
                ${currentStep > step.num ? "w-full" : "w-0"}
              `}
                    />
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-2 md:p-4  bg-black/70 backdrop-blur-sm">
          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-red-500" />
              <span className="text-red-400 text-sm">{error}</span>
            </div>
          )}

          {/* Step 1: Company Type */}

          {currentStep === 1 && (
            <StepCompanyType
              formData={formData}
              updateFormData={updateFormData}
              COMPANY_TYPES={COMPANY_TYPES}
            />
          )}

          {/* Step 2: Company Details */}

          {currentStep === 2 && (
            <StepCompanyInfo
              formData={formData}
              updateFormData={updateFormData}
              setValidationState={setValidationState}
            />
          )}

          {currentStep === 3 && (
            <StepModulesSelection
              modules={modules}
              formData={formData}
              setFormData={setFormData}
            />
          )}

          {currentStep === 4 && (
            <StepReviewSummary formData={formData} modules={modules} />
          )}
        </div>
        {/* Footer */}
        <div className="bg-slate-800 border-t border-slate-700 px-6 py-2 flex justify-between">
          <button
            onClick={currentStep === 1 ? onClose : prevStep}
            className="px-4 py-2 text-slate-400 hover:text-white transition-colors flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />{" "}
            {currentStep === 1 ? "Cancel" : "Back"}
          </button>

          {currentStep < 4 ? (
            <button
              onClick={nextStep}
              className="px-5 py-2 bg-teal-500 hover:bg-teal-600 text-white font-medium rounded-lg transition-colors flex items-center gap-2"
            >
              Continue <ArrowRight className="w-4 h-4" />
            </button>
          ) : (
            <button
              onClick={handleSubmitCompany}
              disabled={loading}
              className="px-5 py-2 bg-teal-500 hover:bg-teal-600 text-white font-medium rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {isEdit ? `updating...` : `Creating...`}
                </>
              ) : (
                <>
                  <Check className="w-4 h-4" />
                  {isEdit ? `Update` : `Create`} Company
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CompanySetup;
